/**
 * PO Escalation — procurement domain operational semantics
 *
 * Derives the escalation state of a Purchase Order from its status and
 * temporal position. Escalation is time-sensitive and dependency-sensitive.
 *
 * Levels (priority order):
 *   overdue          — 'ordered', expectedDeliveryDate < today
 *   stale-approval   — 'pending' for ≥3 days; decision bottleneck
 *   awaiting-approval — 'pending', freshly created
 *   arriving-soon    — 'ordered', expected within 7 days
 *   approved         — approved, not yet ordered
 *   nominal          — 'ordered', comfortable delivery window
 *   draft            — not yet submitted
 *   received         — resolved; goods received
 *   cancelled        — resolved; terminated
 *
 * The `icon` field is a string name resolved by badge components via ICON_MAP.
 */

/**
 * @param {object} po — Purchase Order document from API
 * @returns {{ level, label, sub, icon, rowBg, rowClass, badgeClass, [daysLate], [daysUntil], [daysPending] }}
 */
export function computePOEscalation(po) {
  const today = new Date();
  const { status, expectedDeliveryDate, createdAt } = po;

  // ── Overdue: ordered PO past expected delivery ───────────────────
  if (status === 'ordered' && expectedDeliveryDate) {
    const expected = new Date(expectedDeliveryDate);
    if (expected < today) {
      const daysLate = Math.floor((today - expected) / 86400000);
      return {
        level: 'overdue',
        label: 'Overdue',
        sub: `${daysLate}d late`,
        daysLate,
        icon: 'AlertTriangle',
        rowBg: 'bg-red-50/40 dark:bg-red-950/20',
        rowClass: 'border-l-4 border-l-red-500',
        badgeClass: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
      };
    }
    // Arriving within 7 days
    const daysUntil = Math.ceil((expected - today) / 86400000);
    if (daysUntil <= 7) {
      return {
        level: 'arriving-soon',
        label: `Due in ${daysUntil}d`,
        sub: null,
        daysUntil,
        icon: 'Clock',
        rowBg: 'bg-blue-50/20 dark:bg-blue-950/10',
        rowClass: 'border-l-4 border-l-blue-400',
        badgeClass: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
      };
    }
  }

  // ── Stale approval: pending for 3+ days ──────────────────────────
  if (status === 'pending') {
    const created = createdAt ? new Date(createdAt) : today;
    const daysPending = Math.floor((today - created) / 86400000);
    if (daysPending >= 3) {
      return {
        level: 'stale-approval',
        label: 'Approval stalled',
        sub: `${daysPending}d pending`,
        daysPending,
        icon: 'AlertTriangle',
        rowBg: 'bg-amber-50/30 dark:bg-amber-950/20',
        rowClass: 'border-l-4 border-l-amber-500',
        badgeClass: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
      };
    }
    return {
      level: 'awaiting-approval',
      label: 'Awaiting approval',
      sub: null,
      icon: 'Clock',
      rowBg: 'bg-amber-50/10 dark:bg-amber-950/10',
      rowClass: 'border-l-4 border-l-amber-300',
      badgeClass: 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    };
  }

  // ── Approved: ready to place with supplier ───────────────────────
  if (status === 'approved') {
    return {
      level: 'approved',
      label: 'Approved',
      sub: null,
      icon: 'CheckCircle',
      rowBg: '',
      rowClass: 'border-l-4 border-l-brand-400',
      badgeClass: 'bg-brand-100 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300',
    };
  }

  // ── Received ──────────────────────────────────────────────────────
  if (status === 'received') {
    return {
      level: 'received',
      label: 'Received',
      sub: null,
      icon: 'CheckCircle',
      rowBg: '',
      rowClass: '',
      badgeClass: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
    };
  }

  // ── Cancelled ─────────────────────────────────────────────────────
  if (status === 'cancelled') {
    return {
      level: 'cancelled',
      label: 'Cancelled',
      sub: null,
      icon: 'XCircle',
      rowBg: '',
      rowClass: '',
      badgeClass: 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500',
    };
  }

  // ── Draft ─────────────────────────────────────────────────────────
  if (status === 'draft') {
    return {
      level: 'draft',
      label: 'Draft',
      sub: null,
      icon: 'Package',
      rowBg: '',
      rowClass: '',
      badgeClass: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
    };
  }

  // ── Nominal: ordered, comfortable delivery window ─────────────────
  return {
    level: 'nominal',
    label: 'In transit',
    sub: null,
    icon: 'Package',
    rowBg: '',
    rowClass: '',
    badgeClass: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
  };
}

/**
 * Sort weight by escalation urgency.
 *   pos.sort((a, b) =>
 *     PO_ESCALATION_SORT_ORDER[computePOEscalation(a).level]
 *     - PO_ESCALATION_SORT_ORDER[computePOEscalation(b).level]
 *   )
 */
export const PO_ESCALATION_SORT_ORDER = {
  'overdue': 0,
  'stale-approval': 1,
  'awaiting-approval': 2,
  'arriving-soon': 3,
  'approved': 4,
  'nominal': 5,
  'draft': 6,
  'received': 7,
  'cancelled': 8,
};
